// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Extension Pairing Page
 *
 * Allows users to generate a one-time pairing token to connect
 * a browser extension to their account. Shows connected devices
 * with the ability to revoke access.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { copySensitiveText } from "@/lib/clipboard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ShieldCheck,
  Copy,
  Loader2,
  Monitor,
  Smartphone,
  Clock,
  Trash2,
  CheckCircle,
  AlertTriangle,
  KeyRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { trpcQuery, trpcMutation } from "@/lib/api/trpc";
import { isApiError } from "@/lib/api/client";
import { sendSecretKeyToExtension } from "@/services/extension-key-delivery";
import {
  canVerifyWithExtension,
  verifyPairingWithExtension,
} from "@/services/extension-messaging";
import { useVaultCrypto } from "@/hooks/useVaultCrypto";
import { MasterPasswordPrompt } from "@/components/vault/MasterPasswordPrompt";

// ── Types ────────────────────────────────────────────────────────────

interface PairingTokenResponse {
  token: string;
  expiresAt: string;
}

interface Device {
  id: string;
  deviceName: string;
  deviceType: "extension" | "mobile" | "browser";
  publicKey: string;
  signingPublicKey?: string | null;
  trusted: boolean;
  lastSeen: string | null;
  createdAt: string;
  browser?: string;
}

interface VaultRecord {
  kdfSalt: string;
  encryptedVerificationBlob: string | null;
}

// ── Route ────────────────────────────────────────────────────────────

export const Route = createFileRoute(
  "/_app/_auth/dashboard/extensions/pair"
)({
  component: ExtensionPairPage,
  // Deep-link handshake params set by the extension when it opens this page:
  // source=extension, pk=<X25519 pub>, spk=<Ed25519 pub>, ch=<one-time channel>.
  // All optional so existing Links to this route need no search params.
  validateSearch: (
    search: Record<string, unknown>
  ): {
    source?: string;
    pk?: string;
    spk?: string;
    ch?: string;
  } => ({
    source: typeof search.source === "string" ? search.source : undefined,
    pk: typeof search.pk === "string" ? search.pk : undefined,
    spk: typeof search.spk === "string" ? search.spk : undefined,
    ch: typeof search.ch === "string" ? search.ch : undefined,
  }),
});

// ── Constants ────────────────────────────────────────────────────────

// ── Helpers ──────────────────────────────────────────────────────────

function formatTimeRemaining(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function getDeviceIcon(type: Device["deviceType"]) {
  switch (type) {
    case "mobile":
      return Smartphone;
    case "extension":
    case "browser":
    default:
      return Monitor;
  }
}

function getRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// ── Component ────────────────────────────────────────────────────────

type ConnectState =
  | "idle"
  | "verifying"
  | "awaiting-confirm"
  | "connecting"
  | "connected"
  | "error";

function ExtensionPairPage() {
  const { t } = useTranslation("extensions");

  // Deep-link handshake params (extension-initiated one-click connect).
  const { source, pk, spk, ch } = Route.useSearch();
  const isDeepLink =
    source === "extension" && Boolean(pk) && Boolean(spk) && Boolean(ch);

  // Deep-link connect state machine.
  const [connectState, setConnectState] = useState<ConnectState>("idle");
  const [connectError, setConnectError] = useState<string | null>(null);
  // Register the device at most once across retries / unlock resumes.
  const registeredDeviceId = useRef<string | null>(null);
  const autoStarted = useRef(false);

  // Token state
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  // Device list state
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Revoke state
  const [revokeTarget, setRevokeTarget] = useState<Device | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  // Grant-vault-access (wrapped Secret Key delivery) state
  const [grantingDeviceId, setGrantingDeviceId] = useState<string | null>(null);
  const [grantedDeviceIds, setGrantedDeviceIds] = useState<Set<string>>(
    () => new Set()
  );

  // Vault unlock state. Granting wraps the account Secret Key with this
  // device's private key, which is only available while the vault is unlocked.
  // If it's locked, prompt for the master password instead of erroring, then
  // resume the grant for the pending device.
  const { isUnlocked } = useVaultCrypto();
  const [vaultRecord, setVaultRecord] = useState<VaultRecord | null>(null);
  const [showMasterPassword, setShowMasterPassword] = useState(false);
  // Action to resume after the master-password prompt unlocks the vault. Used by
  // both the manual "Grant vault access" button and the deep-link auto-connect.
  const pendingUnlockAction = useRef<(() => void) | null>(null);

  // Ref for countdown interval
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Load devices ────────────────────────────────────────────────

  const loadDevices = useCallback(async () => {
    try {
      setIsLoadingDevices(true);
      setLoadError(null);
      const result = await trpcQuery<Device[]>("device", "list");
      setDevices(result);
    } catch (err: unknown) {
      const message = isApiError(err) ? err.message : "Failed to load devices";
      setLoadError(message);
    } finally {
      setIsLoadingDevices(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Pre-fetch the vault record so the unlock dialog has the salt + verification
  // blob ready if the user grants access while the vault is locked.
  useEffect(() => {
    if (vaultRecord) return;
    trpcQuery<VaultRecord[]>("vault", "list")
      .then((vaults) => {
        if (vaults?.[0]) setVaultRecord(vaults[0]);
      })
      .catch(() => {
        // Best-effort — handleGrantAccess falls back to a toast if it's missing.
      });
  }, [vaultRecord]);

  // ── Countdown timer ─────────────────────────────────────────────

  useEffect(() => {
    if (expiresAt === null) return;

    function tick() {
      const now = Date.now();
      const remaining = Math.max(
        0,
        Math.floor((expiresAt! - now) / 1000)
      );
      setSecondsRemaining(remaining);

      if (remaining <= 0) {
        setToken(null);
        setExpiresAt(null);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }

    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [expiresAt]);

  // ── Generate token ──────────────────────────────────────────────

  const handleGenerateToken = useCallback(async () => {
    try {
      setIsGenerating(true);
      const result = await trpcMutation<PairingTokenResponse>(
        "device",
        "initiatePairing",
        { deviceName: "Browser Extension" }
      );
      setToken(result.token);
      setExpiresAt(new Date(result.expiresAt).getTime());
    } catch (err: unknown) {
      const message = isApiError(err)
        ? err.message
        : t("pair.generateError", "Failed to generate pairing token");
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  }, [t]);

  // ── Copy token ──────────────────────────────────────────────────

  const handleCopyToken = useCallback(async () => {
    if (!token) return;

    try {
      // SECURITY (Round 4, fix #31): schedules a 30s clipboard auto-wipe
      await copySensitiveText(token);
      toast.success(t("pair.copied", "Token copied to clipboard"));
    } catch {
      // Fallback for insecure contexts
      const textarea = document.createElement("textarea");
      textarea.value = token;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success(t("pair.copied", "Token copied to clipboard"));
    }
  }, [token, t]);

  // ── Revoke device ───────────────────────────────────────────────

  const handleRevoke = useCallback(async () => {
    if (!revokeTarget) return;

    try {
      setIsRevoking(true);
      await trpcMutation("device", "revoke", { deviceId: revokeTarget.id });
      setDevices((prev) => prev.filter((d) => d.id !== revokeTarget.id));
      toast.success(
        t("pair.revokeSuccess", "Device access revoked")
      );
    } catch (err: unknown) {
      const message = isApiError(err)
        ? err.message
        : t("pair.revokeError", "Failed to revoke device");
      toast.error(message);
    } finally {
      setIsRevoking(false);
      setRevokeTarget(null);
    }
  }, [revokeTarget, t]);

  // ── Grant vault access (wrapped Secret Key delivery) ─────────────

  const performGrant = useCallback(
    async (device: Device) => {
      try {
        setGrantingDeviceId(device.id);
        await sendSecretKeyToExtension(device.id, device.publicKey);
        setGrantedDeviceIds((prev) => {
          const next = new Set(prev);
          next.add(device.id);
          return next;
        });
        toast.success(
          t(
            "pair.grantSuccess",
            "Vault access sent — unlock the extension with your master password"
          )
        );
        // Reflect the now-trusted state in the list (the grant flips the device
        // to trusted server-side; without this the UI keeps showing it as untrusted).
        void loadDevices();
      } catch (err: unknown) {
        const message = isApiError(err)
          ? err.message
          : err instanceof Error
            ? err.message
            : t("pair.grantError", "Failed to send vault access");
        toast.error(message);
      } finally {
        setGrantingDeviceId(null);
      }
    },
    [t, loadDevices]
  );

  // Run `action` now if the vault is unlocked, otherwise prompt for the master
  // password and resume it after a successful unlock. Wrapping the account
  // Secret Key for a device needs the vault key in memory, so this is the
  // single human gate for both grant paths. Returns false if it couldn't even
  // prompt (no vault record yet).
  const requireUnlock = useCallback(
    (action: () => void): boolean => {
      if (isUnlocked) {
        action();
        return true;
      }
      if (!vaultRecord) {
        toast.error(
          t("pair.unlockFirst", "Unlock your vault first, then grant access.")
        );
        return false;
      }
      pendingUnlockAction.current = action;
      setShowMasterPassword(true);
      return true;
    },
    [isUnlocked, vaultRecord, t]
  );

  const handleGrantAccess = useCallback(
    (device: Device) => {
      requireUnlock(() => void performGrant(device));
    },
    [requireUnlock, performGrant]
  );

  const handleUnlock = useCallback(() => {
    setShowMasterPassword(false);
    const action = pendingUnlockAction.current;
    pendingUnlockAction.current = null;
    action?.();
  }, []);

  const handleCloseUnlock = useCallback(() => {
    setShowMasterPassword(false);
    pendingUnlockAction.current = null;
    // If the user dismisses the prompt mid-connect, surface a recoverable error
    // rather than leaving the card spinning.
    setConnectState((s) =>
      s === "connecting" || s === "verifying" ? "error" : s
    );
    setConnectError((e) =>
      e ??
      (connectState === "connecting" || connectState === "verifying"
        ? t("pair.connectCancelled", "Unlock cancelled. Try connecting again.")
        : null)
    );
  }, [connectState, t]);

  // ── Deep-link auto-connect (one-click extension pairing) ─────────
  //
  // Register the extension device (bound to its one-time channel) and wrap the
  // account Secret Key to it — the extension polls the server to finish. The
  // register step is idempotent across retries via `registeredDeviceId`.
  const runConnect = useCallback(async () => {
    if (!pk || !spk || !ch) return;
    setConnectError(null);
    setConnectState("connecting");
    try {
      if (!registeredDeviceId.current) {
        const res = await trpcMutation<{ deviceId: string }>(
          "device",
          "registerExtensionDevice",
          {
            deviceName: "Browser Extension",
            publicKey: pk,
            signingPublicKey: spk,
            channel: ch,
          }
        );
        registeredDeviceId.current = res.deviceId;
      }
      await sendSecretKeyToExtension(registeredDeviceId.current, pk);
      setConnectState("connected");
      void loadDevices();
    } catch (err: unknown) {
      const message = isApiError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : t("pair.connectError", "Failed to connect the extension");
      setConnectError(message);
      setConnectState("error");
    }
  }, [pk, spk, ch, loadDevices, t]);

  // Gate the connect behind a vault unlock (master password is the human
  // approval). Returns to an actionable error if the vault can't be prompted.
  const startConnect = useCallback(() => {
    const queued = requireUnlock(() => void runConnect());
    if (!queued) {
      setConnectError(
        t("pair.unlockFirst", "Unlock your vault first, then grant access.")
      );
      setConnectState("error");
    }
  }, [requireUnlock, runConnect, t]);

  // On arrival from the extension deep-link, verify the request really came
  // from the installed extension. If verified, connect automatically (zero
  // clicks). Otherwise fall back to an explicit confirm so a forged deep-link
  // can never silently grant vault access. Runs once.
  useEffect(() => {
    if (!isDeepLink || autoStarted.current) return;
    autoStarted.current = true;

    if (!pk || !spk || !ch) return;

    if (!canVerifyWithExtension()) {
      setConnectState("awaiting-confirm");
      return;
    }

    setConnectState("verifying");
    verifyPairingWithExtension(ch, pk, spk).then((verified) => {
      if (verified) {
        startConnect();
      } else {
        setConnectState("awaiting-confirm");
      }
    });
  }, [isDeepLink, pk, spk, ch, startConnect]);

  // ── Filter extension devices ────────────────────────────────────

  const extensionDevices = devices.filter((d) => d.deviceType === "extension");

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-2xl">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
            {t("pair.title", "Connect Browser Extension")}
          </h1>
        </div>
        <p className="text-muted-foreground mt-1">
          {t(
            "pair.description",
            "Generate a one-time token to securely pair your browser extension with your account. The token expires after 5 minutes."
          )}
        </p>
      </div>

      {/* Deep-link one-click connect card (extension-initiated handshake) */}
      {isDeepLink && (
        <Card className="rounded-lg border bg-card text-card-foreground shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              {t("pair.connectTitle", "Connect your browser extension")}
            </CardTitle>
            <CardDescription>
              {t(
                "pair.connectDescription",
                "Your browser extension is requesting access to your vault. Approve it to finish connecting."
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {connectState === "verifying" && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("pair.connectVerifying", "Verifying your extension…")}
              </div>
            )}

            {connectState === "awaiting-confirm" && (
              <Button
                onClick={startConnect}
                className="rounded-md min-h-[44px]"
              >
                <KeyRound className="h-4 w-4 me-2" />
                {t("pair.connectGrant", "Connect & grant vault access")}
              </Button>
            )}

            {connectState === "connecting" && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("pair.connecting", "Connecting…")}
              </div>
            )}

            {connectState === "connected" && (
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                {t(
                  "pair.connectedReturn",
                  "Connected — return to the extension. Unlock it with your master password."
                )}
              </div>
            )}

            {connectState === "error" && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {connectError ??
                      t("pair.connectError", "Failed to connect the extension")}
                  </span>
                </div>
                <Button
                  variant="outline"
                  onClick={startConnect}
                  className="rounded-md min-h-[44px]"
                >
                  {t("pair.retry", "Retry")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Token Generation Card (manual fallback) */}
      {!isDeepLink && (
      <Card className="rounded-lg border bg-card text-card-foreground shadow-sm mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            {t("pair.generateToken", "Generate Pairing Token")}
          </CardTitle>
          <CardDescription>
            {t(
              "pair.generateDescription",
              "Create a secure token to authenticate your browser extension."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            /* No token yet - show generate button */
            <Button
              onClick={handleGenerateToken}
              disabled={isGenerating}
              className="rounded-md min-h-[44px]"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {t("pair.generating", "Generating...")}
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 me-2" />
                  {t("pair.generateToken", "Generate Pairing Token")}
                </>
              )}
            </Button>
          ) : (
            /* Token generated - show token and countdown */
            <div className="space-y-4">
              {/* Success indicator */}
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                <span>{t("pair.tokenGenerated", "Token generated successfully")}</span>
              </div>

              {/* Token display */}
              <div className="flex items-center gap-2">
                <code
                  className={cn(
                    "flex-1 block rounded-md border bg-muted px-3 py-3",
                    "font-mono text-sm sm:text-base text-foreground",
                    "select-all break-all"
                  )}
                >
                  {token}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyToken}
                  className="rounded-md h-[44px] min-w-[44px] flex-shrink-0"
                  aria-label={t("pair.copyToken", "Copy token")}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              {/* Countdown */}
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                {secondsRemaining > 0 ? (
                  <span
                    className={cn(
                      "text-muted-foreground",
                      secondsRemaining <= 60 && "text-destructive font-medium"
                    )}
                  >
                    {t("pair.expiresIn", {
                      defaultValue: "Expires in {{time}}",
                      time: formatTimeRemaining(secondsRemaining),
                    })}
                  </span>
                ) : (
                  <span className="text-destructive font-medium">
                    {t("pair.tokenExpired", "Token expired")}
                  </span>
                )}
              </div>

              {/* Instructions */}
              <p className="text-sm text-muted-foreground">
                {t(
                  "pair.instructions",
                  "Paste this token in the browser extension popup to complete pairing."
                )}
              </p>

              {/* Generate new token button */}
              <Button
                variant="outline"
                onClick={handleGenerateToken}
                disabled={isGenerating}
                className="rounded-md min-h-[44px]"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                    {t("pair.generating", "Generating...")}
                  </>
                ) : (
                  t("pair.regenerate", "Generate New Token")
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <Separator className="my-6" />

      {/* Connected Devices Section */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-1">
          {t("pair.connectedDevices", "Connected Devices")}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t(
            "pair.grantHelp",
            "After pairing, grant an extension access to your vault. Your Secret Key is encrypted on this device for that extension only — unlock the extension with your master password to finish."
          )}
        </p>

        {isLoadingDevices ? (
          /* Loading skeletons */
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Card
                key={i}
                className="rounded-lg border bg-card shadow-sm"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-md" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-[140px]" />
                      <Skeleton className="h-3 w-[200px]" />
                    </div>
                    <Skeleton className="h-9 w-[80px] rounded-md" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : loadError ? (
          /* Error state */
          <Card className="rounded-lg border bg-card shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 text-destructive">
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">{loadError}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadDevices}
                className="rounded-md mt-3 min-h-[44px]"
              >
                {t("pair.retry", "Retry")}
              </Button>
            </CardContent>
          </Card>
        ) : extensionDevices.length === 0 ? (
          /* Empty state */
          <Card className="rounded-lg border bg-card shadow-sm">
            <CardContent className="p-6 text-center">
              <Monitor className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {t(
                  "pair.noDevices",
                  "No browser extensions connected yet. Generate a pairing token above to get started."
                )}
              </p>
            </CardContent>
          </Card>
        ) : (
          /* Device list */
          <div className="space-y-3">
            {extensionDevices.map((device) => {
              const DeviceIcon = getDeviceIcon(device.deviceType);
              const isGranting = grantingDeviceId === device.id;
              const wasGranted = grantedDeviceIds.has(device.id);
              return (
                <Card
                  key={device.id}
                  className="rounded-lg border bg-card shadow-sm"
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      {/* Device info */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted flex-shrink-0">
                          <DeviceIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-foreground truncate">
                              {device.deviceName}
                            </span>
                            <Badge
                              variant="outline"
                              className="text-xs"
                            >
                              {device.deviceType}
                            </Badge>
                            {device.trusted && (
                              <Badge
                                variant="secondary"
                                className="text-xs gap-1"
                              >
                                <CheckCircle className="h-3 w-3" />
                                {t("pair.trusted", "Trusted")}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            {device.browser && (
                              <span>{device.browser}</span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {t("pair.lastSeen", {
                                defaultValue: "Last seen {{time}}",
                                time: getRelativeTime(device.lastSeen),
                              })}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Grant vault access (wrapped Secret Key delivery) */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGrantAccess(device)}
                          disabled={isGranting}
                          className="rounded-md min-h-[44px]"
                        >
                          {isGranting ? (
                            <>
                              <Loader2 className="h-4 w-4 me-1.5 animate-spin" />
                              {t("pair.granting", "Sending...")}
                            </>
                          ) : wasGranted ? (
                            <>
                              <CheckCircle className="h-4 w-4 me-1.5" />
                              {t("pair.grantAgain", "Resend access")}
                            </>
                          ) : (
                            <>
                              <KeyRound className="h-4 w-4 me-1.5" />
                              {t("pair.grantAccess", "Grant vault access")}
                            </>
                          )}
                        </Button>

                        {/* Revoke button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRevokeTarget(device)}
                          className="rounded-md min-h-[44px] text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4 me-1.5" />
                          {t("pair.revoke", "Revoke")}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Master Password Prompt — shown when granting access while the vault
          is locked, then resumes the pending grant. */}
      {vaultRecord && (
        <MasterPasswordPrompt
          open={showMasterPassword}
          onUnlock={handleUnlock}
          onClose={handleCloseUnlock}
          salt={vaultRecord.kdfSalt}
          verificationBlob={vaultRecord.encryptedVerificationBlob}
        />
      )}

      {/* Revoke Confirmation Dialog */}
      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("pair.revokeConfirm", "Revoke device access?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "pair.revokeConfirmDescription",
                "This will disconnect \"{{deviceName}}\" from your account. The extension will need to be paired again to regain access.",
                { deviceName: revokeTarget?.deviceName ?? "" }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevoking}>
              {t("pair.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              disabled={isRevoking}
              className={cn(
                "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              )}
            >
              {isRevoking ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {t("pair.revoking", "Revoking...")}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 me-2" />
                  {t("pair.revoke", "Revoke")}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
